import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Plus,
  Camera,
  Video,
  PanelBottomClose,
  PanelBottomOpen,
  LayoutGrid,
  Rows,
  Sparkles,
  Star,
  ChevronRight,
  X,
  Pencil,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../context/AuthContext';
import { useFilm } from '../context/FilmContext';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '../hooks/queries';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { API_BASE } from '../config/api';
import { cn } from '../lib/utils';

// M1.5 — camera vocab. Free text on the backend; these are the common picks.
const SHOT_TYPES = [
  { value: 'ESTABLISHING', label: 'Establishing' }, { value: 'WS', label: 'Wide (WS)' }, { value: 'MS', label: 'Medium (MS)' },
  { value: 'MCU', label: 'Medium close-up (MCU)' }, { value: 'CU', label: 'Close-up (CU)' }, { value: 'ECU', label: 'Extreme close-up (ECU)' },
  { value: 'OTS', label: 'Over-the-shoulder (OTS)' }, { value: 'TWO_SHOT', label: 'Two-shot' }, { value: 'INSERT', label: 'Insert' },
  { value: 'POV', label: 'POV' }, { value: 'TRACKING', label: 'Tracking' }, { value: 'AERIAL', label: 'Aerial / drone' },
];
const CAMERA_ANGLES = ['eye level', 'low', 'high', 'overhead', 'dutch', 'worm\'s eye', 'bird\'s eye'];
const CAMERA_MOVEMENTS = ['static', 'pan', 'tilt', 'dolly in', 'dolly out', 'truck', 'handheld', 'steadicam', 'gimbal', 'crane', 'zoom', 'whip pan'];

const API = API_BASE;

const SHOT_STATUSES = [
  { value: 'planned',       label: 'Planned' },
  { value: 'shot',          label: 'Shot' },
  { value: 'covered',       label: 'Covered' },
  { value: 'pickup_needed', label: 'Pickup Needed' },
  { value: 'dropped',       label: 'Dropped' },
];

// Token-based pill colors per status. Matches the broader app palette
// (success / warning / destructive / muted) so the look stays consistent
// with the SceneBoard column colours.
const SHOT_STATUS_PILL = {
  planned:        'bg-muted text-muted-foreground',
  shot:           'bg-warning/15 text-warning',
  covered:        'bg-success/15 text-success',
  pickup_needed:  'bg-destructive/15 text-destructive',
  dropped:        'bg-muted text-muted-foreground/70',
};

/* Schema reality (verified live):
 *
 * scenes: id, scene_number, scene_name, description, status, location,
 *         interior_exterior, time_of_day, start_date, due_date, cast_ids,
 *         crew_ids, film_id, created_at.
 *   MISSING for this design: is_golden, directors_note, script_text/content
 *   → handled with empty/omit + TODO comments below.
 *
 * shots:  id, scene_id, shot_code, description, status, cast_ids, crew_ids,
 *         final_take_number, video_filename, notes, created_at.
 *   MISSING for this design: is_master, lens, aperture, iso, camera_move,
 *         action (we fall back to description), thumbnail_url.
 *   → all rendered as `—` em-dash with TODO comments. No fabrication.
 */

/* ----------------------------- helpers ----------------------------- */

const sceneHeading = (scene) => {
  if (!scene) return '';
  const ie  = (scene.interior_exterior || '').trim();
  const loc = (scene.location || '').trim();
  const tod = (scene.time_of_day || '').trim();
  const parts = [];
  if (ie) parts.push(ie.toUpperCase());
  if (loc) parts.push(loc.toUpperCase());
  const left = parts.join('. ');
  const right = tod ? tod.toUpperCase() : '';
  if (left && right) return `${left} - ${right}`;
  return left || right || (scene.scene_name || '').toUpperCase();
};

// Hash a string to one of N gradient class combinations — used to vary
// sub-shot placeholder backgrounds. Token-based, no hex literals.
const SHOT_GRADIENTS = [
  'bg-gradient-to-br from-primary/40 to-warning/20',
  'bg-gradient-to-br from-chart-2/40 to-primary/20',
  'bg-gradient-to-br from-chart-4/40 to-primary/15',
  'bg-gradient-to-br from-warning/30 to-destructive/15',
  'bg-gradient-to-br from-success/30 to-primary/20',
];
const hashGradient = (id = '') => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return SHOT_GRADIENTS[h % SHOT_GRADIENTS.length];
};

const titleInitial = (s = '') => (s.trim() ? s.trim()[0].toUpperCase() : '?');

/* ----------------------------- subviews ----------------------------- */

const SceneListItem = ({ scene, isActive, onClick }) => {
  const heading = sceneHeading(scene) || (scene.scene_name || `Scene ${scene.scene_number}`).toUpperCase();
  const isGolden = scene.is_golden === true; // field doesn't exist today — TODO when schema gains it
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`sd-scene-list-${scene.id}`}
      className={cn(
        'group relative w-full text-left rounded-xl p-4 transition-colors',
        'border border-transparent',
        isActive
          ? 'bg-accent/60 border-primary/30 ring-1 ring-primary/20'
          : 'hover:border-border hover:bg-accent/40'
      )}
    >
      {isActive && (
        <span aria-hidden className="absolute left-0 top-3 bottom-3 w-1 rounded-full bg-primary" />
      )}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary">
          Scene {scene.scene_number}
        </span>
        {isGolden && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-warning/15 text-warning text-[9px] font-bold tracking-[0.18em] uppercase">
            <Star className="size-2.5 fill-current" /> Golden
          </span>
        )}
      </div>
      <p className="font-semibold text-sm uppercase tracking-tight leading-snug truncate">
        {heading}
      </p>
      {scene.description && (
        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
          {scene.description}
        </p>
      )}
    </button>
  );
};

const SceneListPanel = ({ scenes, activeId, onSelect, filmId }) => (
  <aside
    className="rounded-2xl border border-border bg-card p-5 flex flex-col"
    data-testid="sd-scene-list"
  >
    <div className="mb-4">
      <Link
        to={filmId ? `/films/${filmId}/board` : '/'}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground hover:text-primary transition-colors mb-3"
      >
        <ArrowLeft className="size-3.5" /> Scene Board
      </Link>
      <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">
        Navigation
      </p>
      <h2 className="font-display text-xl font-semibold tracking-tight mt-1">Scene List</h2>
    </div>
    <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-2 max-h-[calc(100vh-280px)]">
      {scenes.length === 0 ? (
        <p className="text-xs text-muted-foreground p-3">No other scenes in this film yet.</p>
      ) : scenes.map((s) => (
        <SceneListItem
          key={s.id}
          scene={s}
          isActive={s.id === activeId}
          onClick={() => onSelect(s)}
        />
      ))}
    </div>
  </aside>
);

/**
 * ShotCard — unified shot card used in the Grid view.
 * No more master / sub-shot distinction; every shot renders the same card.
 * Surfaces shot_code, status pill, description (clamp-2), take number, and
 * a small video icon when the shot has a video_filename.
 */
const ShotCard = ({ shot, scene, onShotClick, isSelected = false }) => {
  const code = shot.shot_code || `${scene.scene_number}?`;
  const status = SHOT_STATUSES.find((s) => s.value === shot.status) || SHOT_STATUSES[0];
  const take = shot.final_take_number;
  const hasVideo = !!(shot.video_filename && shot.video_filename.trim());

  return (
    <button
      type="button"
      onClick={() => onShotClick(shot)}
      data-testid={`sd-shot-${shot.id}`}
      aria-pressed={isSelected}
      className={cn(
        'group flex flex-col rounded-2xl overflow-hidden border bg-card text-left transition-all',
        isSelected
          ? 'border-primary ring-2 ring-primary/30 shadow-md -translate-y-0.5'
          : 'border-border hover:border-primary/40 hover:-translate-y-0.5 hover:shadow-lg'
      )}
    >
      {/* Visual header — kept short so metadata gets the room. */}
      <div className={cn('relative aspect-video flex items-center justify-center', hashGradient(shot.id))}>
        <span className="font-display font-bold text-4xl md:text-5xl text-foreground/15 select-none">
          {titleInitial(code)}
        </span>
        <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-card/85 backdrop-blur text-[10px] font-bold tracking-[0.22em] uppercase">
          Shot {code}
          {shot.shot_type && <span className="font-mono tracking-normal text-primary">{String(shot.shot_type).replace('_', ' ')}</span>}
        </span>
        <span
          className={cn(
            'absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.16em] uppercase',
            SHOT_STATUS_PILL[status.value] || SHOT_STATUS_PILL.planned
          )}
        >
          {status.label}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-2 p-3">
        <p className={cn(
          'text-sm leading-snug line-clamp-2 min-h-[2.5rem]',
          shot.description ? 'text-foreground' : 'text-muted-foreground'
        )}>
          {shot.description || 'No description yet.'}
        </p>

        <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/60">
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            {take ? (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-secondary text-foreground font-mono text-[10px]">
                T{take}
              </span>
            ) : (
              <span className="text-muted-foreground/60 font-mono text-[10px]">— Take</span>
            )}
          </div>
          {hasVideo && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center justify-center size-6 rounded-md bg-success/15 text-success">
                  <Video className="size-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent>{shot.video_filename}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </button>
  );
};

/**
 * ShotRow — list-view variant. Same fields as ShotCard so the two views
 * agree on what they show.
 */
const ShotRow = ({ shot, scene, onShotClick, isSelected = false }) => {
  const code = shot.shot_code || `${scene.scene_number}?`;
  const status = SHOT_STATUSES.find((s) => s.value === shot.status) || SHOT_STATUSES[0];
  const take = shot.final_take_number;
  const hasVideo = !!(shot.video_filename && shot.video_filename.trim());

  return (
    <button
      type="button"
      onClick={() => onShotClick(shot)}
      data-testid={`sd-shot-${shot.id}`}
      aria-pressed={isSelected}
      className={cn(
        'group w-full flex items-center gap-4 rounded-xl border bg-card p-3 transition-colors text-left',
        isSelected
          ? 'border-primary ring-2 ring-primary/30 bg-accent/30'
          : 'border-border hover:border-primary/40 hover:bg-accent/40'
      )}
    >
      <div className={cn('size-14 rounded-lg shrink-0 flex items-center justify-center', hashGradient(shot.id))}>
        <span className="font-display font-bold text-lg text-foreground/40">
          {titleInitial(code)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary">Shot {code}</p>
          <span
            className={cn(
              'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.16em] uppercase',
              SHOT_STATUS_PILL[status.value] || SHOT_STATUS_PILL.planned
            )}
          >
            {status.label}
          </span>
        </div>
        <p className="text-sm font-medium truncate mt-0.5">
          {shot.description || <span className="text-muted-foreground">No description</span>}
        </p>
      </div>
      {take && (
        <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded-md bg-secondary text-foreground font-mono text-[10px] shrink-0">
          T{take}
        </span>
      )}
      {hasVideo && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center justify-center size-6 rounded-md bg-success/15 text-success shrink-0">
              <Video className="size-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{shot.video_filename}</TooltipContent>
        </Tooltip>
      )}
      <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary shrink-0" />
    </button>
  );
};

/**
 * MemberChips — multi-select chip UI for cast/crew assignment.
 * Backed by the per-film cast_members / crew_members tables (UUID arrays
 * on the shot record). Wired against the existing
 * `GET /api/films/:filmId/cast` and `GET /api/films/:filmId/crew`
 * endpoints — no schema changes.
 */
const MemberChips = ({ available, selectedIds = [], onToggle, emptyLabel }) => {
  if (!available || available.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        {emptyLabel}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {available.map((m) => {
        const isSelected = selectedIds.includes(m.id);
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => onToggle(m.id)}
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors border',
              isSelected
                ? 'bg-primary/15 text-primary border-primary/30'
                : 'bg-card text-muted-foreground border-border hover:border-primary/30 hover:text-foreground'
            )}
            data-testid={`sd-member-chip-${m.id}`}
          >
            {isSelected && <X className="size-3" />}
            <span>{m.name}{m.role ? <span className="text-muted-foreground"> · {m.role}</span> : ''}</span>
          </button>
        );
      })}
    </div>
  );
};

/* ----------------------------- Shot Preview ----------------------------- */

/**
 * ShotPreviewPanel — right-rail preview of the currently-selected shot.
 * Read-only summary: hero gradient + shot_code, status pill, description,
 * take number, video filename, notes, cast and crew chips. An explicit
 * "Edit shot" button opens the existing edit dialog (no inline editing
 * here, by design — keeps the form logic and validation in one place).
 *
 * When no shot is selected, renders an empty-state.
 */
const ShotPreviewPanel = ({ shot, scene, filmCast, filmCrew, onEdit }) => {
  const castMap = useMemo(
    () => new Map((filmCast || []).map((m) => [m.id, m])),
    [filmCast],
  );
  const crewMap = useMemo(
    () => new Map((filmCrew || []).map((m) => [m.id, m])),
    [filmCrew],
  );

  if (!shot) {
    return (
      <aside
        className="rounded-2xl border border-border bg-card p-6 flex flex-col items-center justify-center text-center min-h-[420px] lg:sticky lg:top-6 self-start"
        data-testid="sd-shot-preview-empty"
      >
        <span className="size-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
          <Camera className="size-6 text-muted-foreground" />
        </span>
        <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-1">
          Preview
        </p>
        <h3 className="font-display text-base font-bold tracking-tight mb-1">
          No shot selected
        </h3>
        <p className="text-xs text-muted-foreground max-w-[200px]">
          Click any shot to see its preview here.
        </p>
      </aside>
    );
  }

  const code   = shot.shot_code || `${scene.scene_number}?`;
  const status = SHOT_STATUSES.find((s) => s.value === shot.status) || SHOT_STATUSES[0];
  const take   = shot.final_take_number;
  const hasVideo = !!(shot.video_filename && shot.video_filename.trim());

  const castSelected = (shot.cast_ids || []).map((id) => castMap.get(id)).filter(Boolean);
  const crewSelected = (shot.crew_ids || []).map((id) => crewMap.get(id)).filter(Boolean);

  return (
    <aside
      className="rounded-2xl border border-border bg-card flex flex-col overflow-hidden lg:sticky lg:top-6 self-start max-h-[calc(100vh-48px)]"
      data-testid="sd-shot-preview"
    >
      {/* Hero — stays pinned at top while the body scrolls below */}
      <div className={cn('relative aspect-video flex items-center justify-center shrink-0', hashGradient(shot.id))}>
        <span className="font-display font-bold text-6xl text-foreground/15 select-none">
          {titleInitial(code)}
        </span>
        <span className="absolute top-3 left-3 inline-flex items-center px-2 py-0.5 rounded-md bg-card/85 backdrop-blur text-[10px] font-bold tracking-[0.22em] uppercase">
          Shot {code}
        </span>
        <span
          className={cn(
            'absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.16em] uppercase',
            SHOT_STATUS_PILL[status.value] || SHOT_STATUS_PILL.planned,
          )}
        >
          {status.label}
        </span>
      </div>

      {/* Body — bounded scroll region so long notes / large cast & crew
          rosters don't push the panel past the viewport */}
      <div
        className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-5 p-5"
        data-testid="sd-shot-preview-scroll"
      >
        {/* Header row: eyebrow + Edit CTA */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">
              Selected Shot
            </p>
            <h3 className="font-display text-xl font-semibold tracking-tight mt-0.5">
              Shot {code}
            </h3>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onEdit}
            className="rounded-xl shrink-0"
            data-testid="sd-preview-edit"
          >
            <Pencil className="size-3.5 mr-1.5" /> Edit
          </Button>
        </div>

        {/* Description */}
        <div>
          <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-1.5">
            Description
          </p>
          <p className={cn(
            'text-sm leading-relaxed',
            shot.description ? 'text-foreground' : 'text-muted-foreground italic',
          )}>
            {shot.description || 'No description yet.'}
          </p>
        </div>

        {/* Camera (M1.5) */}
        {(shot.shot_type || shot.camera_angle || shot.camera_movement || shot.lens) && (
          <div className="rounded-xl border border-border bg-background p-3" data-testid="sd-shot-camera">
            <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-1">Camera</p>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
              {shot.shot_type && <span><span className="text-muted-foreground">Type</span> <b className="font-medium">{String(shot.shot_type).replace('_', ' ')}</b></span>}
              {shot.camera_angle && <span><span className="text-muted-foreground">Angle</span> <b className="font-medium">{shot.camera_angle}</b></span>}
              {shot.camera_movement && <span><span className="text-muted-foreground">Move</span> <b className="font-medium">{shot.camera_movement}</b></span>}
              {shot.lens && <span><span className="text-muted-foreground">Lens</span> <b className="font-medium">{shot.lens}</b></span>}
            </div>
          </div>
        )}

        {/* Meta row: take + video */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-border bg-background p-3">
            <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-1">
              Take
            </p>
            <p className="font-mono text-sm font-semibold">
              {take ? `T${take}` : <span className="text-muted-foreground/60">—</span>}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-background p-3 min-w-0">
            <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-1">
              Video
            </p>
            {hasVideo ? (
              <div className="flex items-center gap-1.5 min-w-0">
                <Video className="size-3.5 text-success shrink-0" />
                <span className="font-mono text-xs truncate" title={shot.video_filename}>
                  {shot.video_filename}
                </span>
              </div>
            ) : (
              <p className="text-muted-foreground/60 text-sm">—</p>
            )}
          </div>
        </div>

        {/* Notes */}
        {shot.notes && shot.notes.trim() && (
          <div>
            <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-1.5">
              Production Notes
            </p>
            <p className="text-sm leading-relaxed text-foreground/85 whitespace-pre-line">
              {shot.notes}
            </p>
          </div>
        )}

        {/* Cast */}
        <div>
          <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-2">
            Cast ({castSelected.length})
          </p>
          {castSelected.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No cast assigned to this shot.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {castSelected.map((m) => (
                <span
                  key={m.id}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                  data-testid={`sd-preview-cast-${m.id}`}
                >
                  {m.name}
                  {m.role && <span className="text-primary/60 ml-1">· {m.role}</span>}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Crew */}
        <div>
          <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-2">
            Members required ({crewSelected.length})
          </p>
          {crewSelected.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No crew assigned to this shot.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {crewSelected.map((m) => (
                <span
                  key={m.id}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-secondary text-foreground border border-border"
                  data-testid={`sd-preview-crew-${m.id}`}
                >
                  {m.name}
                  {m.role && <span className="text-muted-foreground ml-1">· {m.role}</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

/* ----------------------------- Script Context ----------------------------- */

const ScriptContext = ({ scene, onCollapse }) => {
  const heading = sceneHeading(scene);
  // Schema doesn't have script_text or content — render description as plain
  // paragraphs. TODO(phase-X): replace with structured screenplay rendering
  // (action / character / parenthetical / dialogue) when those fields ship.
  const body = (scene.description || '').trim();

  // Director's note: schema has no scene-level note column. The `notes`
  // table is per-entity but already covered separately. Render the card
  // ONLY when a future scene.directors_note field is populated.
  const directorsNote = (scene.directors_note || '').trim();

  return (
    <section
      className="rounded-2xl border border-border bg-card p-6 md:p-8"
      data-testid="sd-script-context"
    >
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <span className="size-10 rounded-xl bg-secondary flex items-center justify-center">
            <FileText className="size-5 text-primary" />
          </span>
          <div>
            <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">
              Reference
            </p>
            <h2 className="font-display text-xl font-semibold tracking-tight mt-0.5">
              Script Context
            </h2>
          </div>
        </div>
        <button
          type="button"
          onClick={onCollapse}
          aria-label="Collapse script context"
          className="size-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          data-testid="sd-script-collapse"
        >
          <PanelBottomClose className="size-4" />
        </button>
      </div>

      <div className="text-center mb-6">
        <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-info mb-1">
          Scene {scene.scene_number}
        </p>
        <h3 className="font-display text-base font-bold tracking-tight uppercase">
          {heading || (scene.scene_name || `Scene ${scene.scene_number}`).toUpperCase()}
        </h3>
      </div>

      {body ? (
        <div className="space-y-4 text-sm leading-relaxed text-foreground/85 max-w-prose mx-auto">
          {body.split(/\n{2,}/).map((para, i) => (
            <p key={i} className="whitespace-pre-line">{para}</p>
          ))}
        </div>
      ) : (
        <div className="py-8 text-center">
          <p className="text-sm text-muted-foreground">No script context yet for this scene.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add a description to populate this view.
          </p>
        </div>
      )}

      {directorsNote && (
        <div className="mt-6 rounded-xl bg-primary/10 border border-primary/20 p-4 max-w-prose mx-auto" data-testid="sd-directors-note">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="size-4 text-primary" />
            <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary">
              Director&rsquo;s Note
            </p>
          </div>
          <p className="text-sm text-foreground leading-snug">{directorsNote}</p>
        </div>
      )}
    </section>
  );
};

/* ============================== Page ============================== */

const SCRIPT_COLLAPSED_KEY = 'frameflow.scriptContext.collapsed';

const SceneDetail = () => {
  const { sceneId } = useParams();
  const navigate = useNavigate();
  const { invalidateFilm } = useFilm() || {};
  const queryClient = useQueryClient();

  const [scene, setScene] = useState(null);
  const [filmId, setFilmId] = useState(null);
  const [shots, setShots] = useState([]);
  const [siblingScenes, setSiblingScenes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cast / crew rosters for the parent film. Used by the create/edit dialogs
  // (cast_ids and crew_ids on the shot are UUID arrays into these tables).
  const [filmCast, setFilmCast] = useState([]);
  const [filmCrew, setFilmCrew] = useState([]);

  // create-shot dialog (now matches the full schema)
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    shot_code: '',
    description: '',
    status: 'planned',
    final_take_number: '',
    video_filename: '',
    shot_type: '',
    camera_angle: '',
    camera_movement: '',
    lens: '',
    notes: '',
    cast_ids: [],
    crew_ids: [],
  });
  const [creating, setCreating] = useState(false);

  // edit-shot dialog — same shape as create
  const [editingShot, setEditingShot] = useState(null);
  const [editForm, setEditForm] = useState({
    shot_code: '',
    description: '',
    status: 'planned',
    final_take_number: '',
    video_filename: '',
    shot_type: '',
    camera_angle: '',
    camera_movement: '',
    lens: '',
    notes: '',
    cast_ids: [],
    crew_ids: [],
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // view layout toggle
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  // selected shot for the right-rail preview panel
  const [selectedShotId, setSelectedShotId] = useState(null);

  // right column collapse
  const [scriptCollapsed, setScriptCollapsed] = useState(() => {
    try { return localStorage.getItem(SCRIPT_COLLAPSED_KEY) === '1'; } catch (err) {
      if (import.meta.env.DEV) console.warn('[SceneDetail] script-collapsed read failed:', err);
      return false;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(SCRIPT_COLLAPSED_KEY, scriptCollapsed ? '1' : '0'); } catch (err) {
      if (import.meta.env.DEV) console.warn('[SceneDetail] script-collapsed write failed:', err);
    }
  }, [scriptCollapsed]);

  /* ----- data ----- */

  // [C] cancels the in-flight scene bundle on unmount / sceneId change.
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const [sceneRes, shotsRes] = await Promise.all([
        axios.get(`${API}/scenes/${sceneId}`,         { headers: getAuthHeaders(), signal: controller.signal }),
        axios.get(`${API}/scenes/${sceneId}/shots`,   { headers: getAuthHeaders(), signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;
      setScene(sceneRes.data);
      setFilmId(sceneRes.data.film_id);
      setShots(shotsRes.data || []);
      // siblings
      try {
        const sib = await axios.get(`${API}/films/${sceneRes.data.film_id}/scenes`, { headers: getAuthHeaders(), signal: controller.signal });
        if (controller.signal.aborted) return;
        const sorted = [...(sib.data || [])].sort((a, b) => {
          const an = String(a.scene_number || '');
          const bn = String(b.scene_number || '');
          return an.localeCompare(bn, undefined, { numeric: true });
        });
        setSiblingScenes(sorted);
      } catch {
        if (!controller.signal.aborted) setSiblingScenes([]);
      }
    } catch (err) {
      if (controller.signal.aborted || axios.isCancel?.(err)) return;
      console.error('Failed to load scene:', err);
      toast.error('Failed to load scene');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [sceneId]);

  useEffect(() => {
    fetchData();
    return () => { abortRef.current?.abort(); };
  }, [fetchData]);

  // Load cast/crew once we know the film. Soft-fail — the dialog will
  // show an empty-state hint if either roster fails or is empty.
  useEffect(() => {
    if (!filmId) return;
    let cancelled = false;
    (async () => {
      try {
        const [castRes, crewRes] = await Promise.all([
          axios.get(`${API}/films/${filmId}/cast`, { headers: getAuthHeaders() }),
          axios.get(`${API}/films/${filmId}/crew`, { headers: getAuthHeaders() }),
        ]);
        if (cancelled) return;
        setFilmCast(castRes.data || []);
        setFilmCrew(crewRes.data || []);
      } catch (e) {
        // surface as console-only — dialog renders empty roster gracefully
        console.warn('Failed to load film cast/crew', e?.response?.status);
      }
    })();
    return () => { cancelled = true; };
  }, [filmId]);

  /* ----- derivations ----- */

  // All shots, sorted by shot_code (numeric-aware). No master / sub split anymore.
  const sortedShots = useMemo(() => {
    return [...shots].sort((a, b) => {
      const ac = String(a.shot_code || '');
      const bc = String(b.shot_code || '');
      return ac.localeCompare(bc, undefined, { numeric: true });
    });
  }, [shots]);

  // Auto-select first shot when shots load / scene changes. Selection sticks
  // until another shot is clicked. If the selected shot disappears (e.g. after
  // a delete or refresh), fall back to the first available shot.
  useEffect(() => {
    if (sortedShots.length === 0) {
      setSelectedShotId(null);
      return;
    }
    setSelectedShotId((prev) => {
      if (prev && sortedShots.some((s) => s.id === prev)) return prev;
      return sortedShots[0].id;
    });
  }, [sortedShots]);

  const selectedShot = useMemo(
    () => sortedShots.find((s) => s.id === selectedShotId) || null,
    [sortedShots, selectedShotId],
  );

  /* ----- handlers ----- */

  const resetCreateForm = () => setCreateForm({
    shot_code: '',
    description: '',
    status: 'planned',
    final_take_number: '',
    video_filename: '',
    shot_type: '',
    camera_angle: '',
    camera_movement: '',
    lens: '',
    notes: '',
    cast_ids: [],
    crew_ids: [],
  });

  const handleCreateShot = async (e) => {
    e.preventDefault();
    if (!createForm.shot_code.trim()) {
      toast.error('Shot code is required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        shot_code: createForm.shot_code.trim(),
        description: createForm.description || null,
        status: createForm.status,
        final_take_number: createForm.final_take_number || null,
        video_filename: createForm.video_filename || null,
        shot_type: createForm.shot_type || null,
        camera_angle: createForm.camera_angle || null,
        camera_movement: createForm.camera_movement || null,
        lens: createForm.lens || null,
        notes: createForm.notes || null,
        cast_ids: createForm.cast_ids,
        crew_ids: createForm.crew_ids,
      };
      await axios.post(`${API}/scenes/${sceneId}/shots`, payload, { headers: getAuthHeaders() });
      resetCreateForm();
      setCreateOpen(false);
      toast.success('Shot created');
      // Bust the parent film cache so navigating back to Film Dashboard
      // shows the updated shot count (no stale numbers).
      if (filmId) {
        try { invalidateFilm && invalidateFilm(filmId); } catch (err) { console.warn('[SceneDetail] invalidateFilm failed:', err); }
        // [M] keep the RQ-migrated Film Dashboard / Scene Board counts fresh
        queryClient.invalidateQueries({ queryKey: qk.film(filmId) });
        queryClient.invalidateQueries({ queryKey: qk.filmScenes(filmId) });
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create shot');
    } finally {
      setCreating(false);
    }
  };

  const onShotClick = (shot) => {
    // Selecting a shot only updates the right-rail preview now —
    // editing is triggered explicitly via the preview panel's "Edit"
    // button (handleEditSelectedShot).
    setSelectedShotId(shot.id);
  };

  const handleEditSelectedShot = () => {
    if (!selectedShot) return;
    setEditingShot(selectedShot);
    setEditForm({
      shot_code: selectedShot.shot_code || '',
      description: selectedShot.description || '',
      status: selectedShot.status || 'planned',
      final_take_number: selectedShot.final_take_number || '',
      video_filename: selectedShot.video_filename || '',
      shot_type: selectedShot.shot_type || '',
      camera_angle: selectedShot.camera_angle || '',
      camera_movement: selectedShot.camera_movement || '',
      lens: selectedShot.lens || '',
      notes: selectedShot.notes || '',
      cast_ids: Array.isArray(selectedShot.cast_ids) ? [...selectedShot.cast_ids] : [],
      crew_ids: Array.isArray(selectedShot.crew_ids) ? [...selectedShot.crew_ids] : [],
    });
  };

  const handleSaveEditShot = async () => {
    if (!editingShot) return;
    if (!editForm.shot_code.trim()) {
      toast.error('Shot code is required');
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {
        shot_code: editForm.shot_code.trim(),
        description: editForm.description || null,
        status: editForm.status,
        final_take_number: editForm.final_take_number || null,
        video_filename: editForm.video_filename || null,
        shot_type: editForm.shot_type || null,
        camera_angle: editForm.camera_angle || null,
        camera_movement: editForm.camera_movement || null,
        lens: editForm.lens || null,
        notes: editForm.notes || null,
        cast_ids: editForm.cast_ids,
        crew_ids: editForm.crew_ids,
      };
      await axios.put(`${API}/shots/${editingShot.id}`, payload, { headers: getAuthHeaders() });
      toast.success('Shot updated');
      setEditingShot(null);
      // Bust the parent film cache so the Dashboard's covered/uncovered
      // count is fresh when the user navigates back.
      if (filmId) {
        try { invalidateFilm && invalidateFilm(filmId); } catch (err) { console.warn('[SceneDetail] invalidateFilm failed:', err); }
        // [M] keep the RQ-migrated Film Dashboard / Scene Board counts fresh
        queryClient.invalidateQueries({ queryKey: qk.film(filmId) });
        queryClient.invalidateQueries({ queryKey: qk.filmScenes(filmId) });
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update shot');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSelectScene = (s) => {
    if (s.id === sceneId) return;
    navigate(`/scenes/${s.id}`);
  };

  // Toggle helper used by both create and edit dialogs.
  const toggleId = (list, id) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  /* ----- render ----- */

  if (loading && !scene) {
    return (
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-10" data-testid="sd-loading">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)_360px] gap-6">
          <Skeleton className="h-[400px] rounded-2xl" />
          <Skeleton className="h-[600px] rounded-2xl" />
          <Skeleton className="h-[400px] rounded-2xl hidden lg:block" />
        </div>
      </div>
    );
  }

  if (!scene) {
    return (
      <div className="container mx-auto px-4 md:px-6 py-20 text-center text-destructive">
        Scene not found
      </div>
    );
  }

  const sceneTitleHeading = sceneHeading(scene) || (scene.scene_name || `Scene ${scene.scene_number}`).toUpperCase();

  return (
    <div className="bg-background flex-1 min-w-0 relative" data-testid="scene-detail">
      <main className="container mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
        {/* ----- Top: 3-column grid (Scene list | Shots | Selected Shot Preview) ----- */}
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)_360px]">
          {/* ----- Column 1: Scene List ----- */}
          <SceneListPanel
            scenes={siblingScenes}
            activeId={scene.id}
            onSelect={handleSelectScene}
            filmId={filmId}
          />

          {/* ----- Column 2: Shots ----- */}
          <section
            className="rounded-2xl border border-border bg-card p-5 md:p-6 min-w-0 flex flex-col"
            data-testid="sd-shot-breakdown"
          >
            <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div className="min-w-0">
                {/* INT./EXT. — LOCATION — TIME OF DAY: useful, kept. */}
                <p className="text-xs font-bold tracking-[0.22em] uppercase text-foreground/80">
                  {sceneTitleHeading}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="inline-flex items-center rounded-xl border border-border bg-card overflow-hidden h-9">
                  <button
                    type="button"
                    onClick={() => setViewMode('grid')}
                    aria-label="Grid view"
                    aria-pressed={viewMode === 'grid'}
                    data-testid="sd-view-grid"
                    className={cn(
                      'size-9 flex items-center justify-center transition-colors',
                      viewMode === 'grid' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <LayoutGrid className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    aria-label="List view"
                    aria-pressed={viewMode === 'list'}
                    data-testid="sd-view-list"
                    className={cn(
                      'size-9 flex items-center justify-center transition-colors border-l border-border',
                      viewMode === 'list' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Rows className="size-4" />
                  </button>
                </div>
              </div>
            </header>

            {sortedShots.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
                <span className="size-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
                  <Camera className="size-6 text-primary" />
                </span>
                <h3 className="font-display text-xl font-bold tracking-tight mb-1">
                  No shots yet for this scene.
                </h3>
                <p className="text-sm text-muted-foreground mb-5">
                  Add a shot to start the breakdown.
                </p>
                <Button onClick={() => setCreateOpen(true)} className="rounded-xl" data-testid="sd-empty-add-shot">
                  <Plus className="size-4 mr-1.5" /> Add Shot
                </Button>
              </div>
            ) : (
              // Constrained scrollable region — keeps the shots column from
              // growing unbounded as the shot count rises. Matches the
              // scene-list panel's overflow pattern so the three top columns
              // stay visually aligned.
              <div
                className="flex-1 overflow-y-auto -mx-1 px-1 max-h-[calc(100vh-260px)]"
                data-testid="sd-shots-scroll"
              >
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {sortedShots.map((s) => (
                      <ShotCard
                        key={s.id}
                        shot={s}
                        scene={scene}
                        onShotClick={onShotClick}
                        isSelected={s.id === selectedShotId}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sortedShots.map((s) => (
                      <ShotRow
                        key={s.id}
                        shot={s}
                        scene={scene}
                        onShotClick={onShotClick}
                        isSelected={s.id === selectedShotId}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ----- Column 3: Selected Shot Preview ----- */}
          <ShotPreviewPanel
            shot={selectedShot}
            scene={scene}
            filmCast={filmCast}
            filmCrew={filmCrew}
            onEdit={handleEditSelectedShot}
          />
        </div>

        {/* ----- Bottom: full-width Reference Script Context ----- */}
        {!scriptCollapsed && (
          <ScriptContext
            scene={scene}
            onCollapse={() => setScriptCollapsed(true)}
          />
        )}

        {/* Collapsed: show a discreet "Show script context" pill */}
        {scriptCollapsed && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setScriptCollapsed(false)}
              aria-label="Expand script context"
              data-testid="sd-script-expand"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-card text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors shadow-sm"
            >
              <PanelBottomOpen className="size-4" />
              Show script context
            </button>
          </div>
        )}
      </main>

      {/* Floating Add-Shot FAB — single round button, single plus icon. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            aria-label="Add shot to this scene"
            data-testid="sd-fab-add-shot"
            className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-warning text-warning-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
          >
            <Plus className="size-6" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">Add shot to this scene</TooltipContent>
      </Tooltip>

      {/* Create Shot Dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(o) => { setCreateOpen(o); if (!o) resetCreateForm(); }}
      >
        <DialogContent
          data-testid="sd-create-shot-dialog"
          className="max-w-xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>Add Shot to Scene {scene.scene_number}</DialogTitle>
          </DialogHeader>
          <ShotForm
            form={createForm}
            setForm={setCreateForm}
            scene={scene}
            filmCast={filmCast}
            filmCrew={filmCrew}
            toggleId={toggleId}
            mode="create"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreateShot} disabled={creating} data-testid="sd-submit-shot">
              {creating ? 'Creating…' : 'Create Shot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Shot Dialog */}
      <Dialog open={!!editingShot} onOpenChange={(o) => { if (!o) setEditingShot(null); }}>
        <DialogContent
          data-testid="sd-edit-shot-dialog"
          className="max-w-xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>Shot {editingShot?.shot_code || ''}</DialogTitle>
          </DialogHeader>
          {editingShot && (
            <ShotForm
              form={editForm}
              setForm={setEditForm}
              scene={scene}
              filmCast={filmCast}
              filmCrew={filmCrew}
              toggleId={toggleId}
              mode="edit"
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingShot(null)} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={handleSaveEditShot} disabled={savingEdit} data-testid="sd-save-edit">
              {savingEdit ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * ShotForm — shared form body for create + edit dialogs. Renders every
 * schema-supported field so the two dialogs are symmetric.
 *
 * Schema audit (live, /app/backend/models/shot.py + Supabase shots table):
 *   shot_code (text, required), description (text), status (enum),
 *   final_take_number (text), video_filename (text), notes (text),
 *   cast_ids (UUID[]), crew_ids (UUID[]), scene_id, created_at.
 *
 * `cast_ids` / `crew_ids` reference the per-film cast_members /
 * crew_members tables. We render them as toggleable chips backed by
 * GET /api/films/{filmId}/cast and /api/films/{filmId}/crew.
 *
 * We label crew as "Members required" per the brief — the column itself
 * stays as `crew_ids` so no schema changes are needed.
 */
const ShotForm = ({ form, setForm, scene, filmCast, filmCrew, toggleId, mode }) => {
  const idPrefix = mode === 'edit' ? 'edit' : 'create';
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-shot-code`}>Shot Code <span className="text-destructive">*</span></Label>
          <Input
            id={`${idPrefix}-shot-code`}
            placeholder={`e.g., ${scene.scene_number}A`}
            value={form.shot_code}
            onChange={(e) => setForm((f) => ({ ...f, shot_code: e.target.value }))}
            required
            data-testid={`sd-${idPrefix}-shot-code-input`}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-shot-status`}>Status</Label>
          <Select
            value={form.status}
            onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
          >
            <SelectTrigger id={`${idPrefix}-shot-status`} data-testid={`sd-${idPrefix}-status-select`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SHOT_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-shot-desc`}>Description</Label>
        <Textarea
          id={`${idPrefix}-shot-desc`}
          rows={3}
          placeholder="Describe the shot, camera, and action."
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          data-testid={`sd-${idPrefix}-desc-input`}
        />
      </div>

      {/* M1.5 — camera fields */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-shot-type`}>Shot type</Label>
          <Select value={form.shot_type || '__none'} onValueChange={(v) => setForm((f) => ({ ...f, shot_type: v === '__none' ? '' : v }))}>
            <SelectTrigger id={`${idPrefix}-shot-type`} data-testid={`sd-${idPrefix}-type-select`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">—</SelectItem>
              {SHOT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-shot-angle`}>Angle</Label>
          <Input id={`${idPrefix}-shot-angle`} list={`${idPrefix}-angles`} placeholder="eye level" value={form.camera_angle}
            onChange={(e) => setForm((f) => ({ ...f, camera_angle: e.target.value }))} data-testid={`sd-${idPrefix}-angle-input`} />
          <datalist id={`${idPrefix}-angles`}>{CAMERA_ANGLES.map((a) => <option key={a} value={a} />)}</datalist>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-shot-move`}>Movement</Label>
          <Input id={`${idPrefix}-shot-move`} list={`${idPrefix}-moves`} placeholder="static" value={form.camera_movement}
            onChange={(e) => setForm((f) => ({ ...f, camera_movement: e.target.value }))} data-testid={`sd-${idPrefix}-move-input`} />
          <datalist id={`${idPrefix}-moves`}>{CAMERA_MOVEMENTS.map((m) => <option key={m} value={m} />)}</datalist>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-shot-lens`}>Lens</Label>
          <Input id={`${idPrefix}-shot-lens`} placeholder="35mm" value={form.lens}
            onChange={(e) => setForm((f) => ({ ...f, lens: e.target.value }))} data-testid={`sd-${idPrefix}-lens-input`} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-shot-take`}>Take Number</Label>
          <Input
            id={`${idPrefix}-shot-take`}
            placeholder="e.g., 3"
            value={form.final_take_number}
            onChange={(e) => setForm((f) => ({ ...f, final_take_number: e.target.value }))}
            data-testid={`sd-${idPrefix}-take-input`}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-shot-video`}>Video File</Label>
          <Input
            id={`${idPrefix}-shot-video`}
            placeholder="e.g., scene1_shot_a.mov"
            value={form.video_filename}
            onChange={(e) => setForm((f) => ({ ...f, video_filename: e.target.value }))}
            data-testid={`sd-${idPrefix}-video-input`}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-shot-notes`}>Notes</Label>
        <Textarea
          id={`${idPrefix}-shot-notes`}
          rows={2}
          placeholder="Production notes for this shot."
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          data-testid={`sd-${idPrefix}-notes-input`}
        />
      </div>

      <div className="space-y-2">
        <Label>Cast</Label>
        <MemberChips
          available={filmCast}
          selectedIds={form.cast_ids}
          onToggle={(id) => setForm((f) => ({ ...f, cast_ids: toggleId(f.cast_ids, id) }))}
          emptyLabel="No cast added to this film yet. Manage cast from Film Management."
        />
      </div>

      <div className="space-y-2">
        <Label>Members required</Label>
        <MemberChips
          available={filmCrew}
          selectedIds={form.crew_ids}
          onToggle={(id) => setForm((f) => ({ ...f, crew_ids: toggleId(f.crew_ids, id) }))}
          emptyLabel="No crew added to this film yet. Manage crew from Film Management."
        />
      </div>
    </div>
  );
};

export default SceneDetail;
